import SectionIntro, {type SectionIntroProps} from '../SectionIntro/SectionIntro'
import SectionHero, {type SectionHeroProps} from '../SectionHero/SectionHero'
import {Accordion, Grid, Heading, Image, Link, Pillar, Testimonial, Text} from '@primer/react-brand'
import {useState} from 'react'
import useFootnotes from '../../hooks/useFootnotes'
import type {FootnoteProps} from '../Footnote/Footnote'
import Footnote from '../Footnote/Footnote'
import FootnoteToggle from '../Footnote/FootnoteToggle/FootnoteToggle'

export interface Customer {
  logo: {
    url: string
    alt: string
    height: number
  }
  description: string
  link: {
    url: string
    label: string
  }
}

interface SectionTemplateProps {
  intro: SectionIntroProps
  hero: SectionHeroProps
  customers?: Customer[]
  testimonial?: {
    quote: string
    name: string
    position: string
  }
  content: {
    title: string
    description: string
    footnote?: Omit<FootnoteProps, 'isVisible'>
    link: {
      url: string
      label: string
    }
  }
  accordion: Array<{
    title: string
    description: string
    link: {
      url: string
      label: string
    }
    visual: {
      url: string
      alt: string
    }
  }>
}

const SectionTemplate: React.FC<SectionTemplateProps> = ({intro, hero, content, customers, testimonial, accordion}) => {
  const [currentAccordionItem, setCurrentAccordionItem] = useState<number>(0)
  const [lastAccordionItem, setLastAccordionItem] = useState<number>(-1)

  const {footnoteStates, onFootnoteClick, onFootnoteEnter, onFootnoteKeyDown} = useFootnotes()

  return (
    <div className="lp-SectionTemplate">
      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content">
          <SectionIntro {...intro} />
          <SectionHero {...hero} />
        </div>
      </div>

      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content lp-SectionBlock-content--lines">
          <Grid className="lp-SectionTemplate-grid">
            <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: testimonial ? 6 : 7}}>
              <div className="lp-SectionTemplate-content">
                <Heading as="h3" size="6" weight="semibold" className="lp-SectionTemplate-content-title">
                  {content.title}
                  {content.footnote && (
                    <FootnoteToggle
                      number={content.footnote.number}
                      onClick={onFootnoteClick}
                      onEnter={onFootnoteEnter}
                      onKeyDown={onFootnoteKeyDown}
                    />
                  )}
                  <span>{content.description}</span>
                </Heading>

                {content.footnote && (
                  <Footnote {...content.footnote} isVisible={!!footnoteStates[content.footnote.number]} />
                )}

                <div>
                  <Link href={content.link.url} variant="accent" className="lp-SectionTemplate-content-link">
                    <Text weight="normal">{content.link.label}</Text>
                  </Link>
                </div>
              </div>
            </Grid.Column>

            <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: testimonial ? 6 : 5}}>
              {testimonial && (
                <div className="lp-SectionTemplate-testimonial">
                  <Testimonial quoteMarkColor="green">
                    <Testimonial.Quote>{testimonial.quote}</Testimonial.Quote>
                    <Testimonial.Name position={testimonial.position}>{testimonial.name}</Testimonial.Name>
                  </Testimonial>
                </div>
              )}
              {customers &&
                customers.map((customer, index) => (
                  <div
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={`customer_${index}`}
                    className="lp-SectionTemplate-customer"
                  >
                    <Pillar>
                      <Pillar.Icon
                        className="lp-SectionTemplate-customer-logo"
                        icon={<Image src={customer.logo.url} alt={customer.logo.alt} height={customer.logo.height} />}
                      />
                      <Pillar.Description className="lp-SectionTemplate-customer-description">
                        <Text weight="light">{customer.description}</Text>
                      </Pillar.Description>
                      <Pillar.Link className="lp-SectionTemplate-customer-link" href={customer.link.url}>
                        <Text weight="normal">{customer.link.label}</Text>
                      </Pillar.Link>
                    </Pillar>
                  </div>
                ))}
            </Grid.Column>
          </Grid>
        </div>
      </div>

      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content">
          <Grid className="lp-SectionTemplate-grid">
            <Grid.Column
              className="lp-SectionTemplate-grid-column lp-SectionTemplate-grid-column--noDesktopBorder lp-SectionTemplate-accordionVisual"
              span={{xsmall: 12, medium: 6}}
            >
              {accordion.map((item, index) => (
                <Image
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`accordion_${index}`}
                  src={item.visual.url}
                  alt=""
                  className={`
                    ${currentAccordionItem === index ? 'lp-SectionTemplate-accordionVisual-image--current' : ''}
                    ${lastAccordionItem === index ? 'lp-SectionTemplate-accordionVisual-image--last' : ''}
                  `}
                />
              ))}
            </Grid.Column>

            <Grid.Column
              className="lp-SectionTemplate-grid-column lp-SectionTemplate-accordionWrapper"
              span={{xsmall: 12, medium: 6}}
            >
              <div className="lp-SectionTemplate-accordion">
                {accordion.map((item, index) => (
                  <Accordion
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={`accordion_${index}`}
                    className="lp-SectionTemplate-accordionItem"
                    open={currentAccordionItem === index}
                    // TODO: Animation isn't smooth, look if can be improved without custom component
                    onClick={({target}) => {
                      const accordionItem = (target as HTMLElement).closest('.lp-SectionTemplate-accordionItem')
                      const isOpen = accordionItem?.hasAttribute('open')

                      if (!accordionItem || isOpen) return

                      setLastAccordionItem(currentAccordionItem)
                      setCurrentAccordionItem(index)
                    }}
                  >
                    <Accordion.Heading>{item.title}</Accordion.Heading>
                    <Accordion.Content className="lp-SectionTemplate-accordionContent">
                      <Text as="p">
                        {/* eslint-disable-next-line react/no-danger */}
                        <span dangerouslySetInnerHTML={{__html: item.description}} />
                      </Text>
                      {/* Accessibility: Reads image alt text as part of the accordion expansion */}
                      <span className="sr-only">{item.visual.alt}</span>
                      <Link href={item.link.url}>{item.link.label}</Link>
                    </Accordion.Content>
                  </Accordion>
                ))}
              </div>
            </Grid.Column>
          </Grid>
        </div>
      </div>
    </div>
  )
}

export default SectionTemplate

try{ SectionTemplate.displayName ||= 'SectionTemplate' } catch {}